import { EyeOff, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import carteraService from '@/services/carteraService'
import { mensajeDeError } from '@/lib/errores'
import { useConfirmacion } from '@/hooks/useConfirmacion'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ESTADO_ADDENDA, dinero, fecha } from './formato'

/** Pestaña «Historial»: los cambios de paquete (addendas) y la bitácora de ocultación de áreas. */
export default function Historial({ ficha, onCambio }) {
  const addendas = ficha.addendas || []
  const bitacora = ficha.ocultacionBitacora || []
  const meta = ficha.catalogo?.areaMeta || {}
  const { confirmar, dialogo } = useConfirmacion()
  // El editor solo puede trabajar UNA addenda a la vez (la más reciente sin aplicar). Las
  // anteriores que ya no van se descartan desde aquí; si no, quedan aceptadas para siempre y la
  // ficha avisa de un conflicto que el asesor no tiene dónde resolver.
  const descartar = async (a) => {
    const seguir = await confirmar({
      titulo: 'Descartar la addenda',
      descripcion: `La addenda del ${fecha(a.createdAt)} deja de existir: el cambio no se aplicará.`,
      textoConfirmar: 'Descartar',
      variante: 'destructiva',
    })
    if (!seguir) return
    try {
      await carteraService.descartarAddenda(a._id)
      toast.success('Addenda descartada')
      onCambio?.()
    } catch (e) {
      toast.error(mensajeDeError(e, 'No se pudo descartar'))
    }
  }
  if (addendas.length === 0 && bitacora.length === 0) {
    return <Card className="p-6 text-center text-neutral-500 dark:text-neutral-400">Sin cambios registrados.</Card>
  }
  return (
    <div className="space-y-4">
      {dialogo}
      {addendas.length > 0 && (
        <Card className="p-4 sm:p-5">
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-3">Cambios de paquete</h3>
          <div className="space-y-2.5">
            {addendas.map((a) => {
              const est = ESTADO_ADDENDA[a.estado] || { label: a.estado, variant: 'secondary' }
              return (
                <div key={a._id} className="flex items-start justify-between gap-3 border-b border-neutral-100 dark:border-neutral-800 pb-2.5">
                  <div className="min-w-0">
                    <p className="text-body-sm text-neutral-800 dark:text-neutral-200">
                      {a.cambio?.antes?.edicion || '—'} → {a.edicion}
                      {' · '}
                      {dinero(a.cambio?.antes?.precioMensual, a.moneda)} → {dinero(a.precioMensual, a.moneda)}
                    </p>
                    <p className="text-caption text-neutral-500 dark:text-neutral-400">
                      {fecha(a.createdAt)}
                      {a.cambio?.aplicadaAt && ` · aplicada ${fecha(a.cambio.aplicadaAt)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={est.variant}>{est.label}</Badge>
                    {a.estado !== 'aplicada' && (
                      <Button variant="ghost" size="sm" onClick={() => descartar(a)} title="Descartar esta addenda">
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>
      )}
      {bitacora.length > 0 && (
        <Card className="p-4 sm:p-5">
          <h3 className="font-semibold text-neutral-900 dark:text-neutral-50 mb-3">Ocultación de áreas</h3>
          <div className="space-y-2">
            {bitacora.map((b, idx) => (
              <p key={idx} className="text-caption text-neutral-600 dark:text-neutral-400 flex items-center gap-1.5">
                <EyeOff size={12} className="text-neutral-400 dark:text-neutral-500" />
                {fecha(b.at)} · {b.por} · [{(b.areas || []).map((a) => meta[a]?.label || a).join(', ') || 'ninguna'}]
              </p>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}
